import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Copy, CheckCircle2, QrCode, CreditCard, MessageCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { useCart } from "@/context/CartContext";
import { api, fileUrl, brl, formatApiError } from "@/lib/api";

const EMPTY = {
  name: "", cpf: "", email: "", email_confirm: "", phone: "", shipping_method: "retirada",
  address: "", cep: "", street: "", number: "", neighborhood: "", city: "", state: "", complement: ""
};

const digits = (v) => (v || "").replace(/\D/g, "");
const validEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || "");
const validCpf = (cpf) => {
  const nums = digits(cpf);
  if (nums.length !== 11 || nums === nums[0]?.repeat(11)) return false;
  for (const size of [9, 10]) {
    let total = 0;
    for (let i = 0; i < size; i++) total += Number(nums[i]) * (size + 1 - i);
    let d = (total * 10) % 11;
    if (d === 10) d = 0;
    if (d !== Number(nums[size])) return false;
  }
  return true;
};

export default function CheckoutPage() {
  const { settings } = useSite();
  const { items, total, clear } = useCart();
  const navigate = useNavigate();
  const [buyer, setBuyer] = useState(EMPTY);
  const [method, setMethod] = useState("pix");
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    document.title = "Checkout | Loja do Evento";
    api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => setConfig({ connected: false, campaign_open: false }));
  }, []);

  // Poll payment status
  useEffect(() => {
    if (!result?.order_id) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/orders/${result.order_id}/status`);
        setStatus(data.payment_status);
        if (["approved", "processed", "accredited"].includes(data.payment_status)) {
          clearInterval(t);
          toast.success("Pagamento confirmado!");
          clear();
        }
      } catch {}
    }, 5000);
    return () => clearInterval(t);
  }, [result]);

  const set = (k) => (e) => setBuyer((b) => ({ ...b, [k]: e.target.value }));

  const campaignOpen = config?.campaign_open;
  const connected = config?.connected;
  const deliveryFee = buyer.shipping_method === "entrega" ? Number(settings?.store_delivery_fee || 0) : 0;
  const finalTotal = Number(total || 0) + deliveryFee;

  const validate = () => {
    if (!buyer.name || buyer.name.trim().split(/\s+/).length < 2) {
      toast.error("Informe o nome completo do comprador.");
      return false;
    }
    if (!validCpf(buyer.cpf)) {
      toast.error("Informe um CPF válido.");
      return false;
    }
    if (!validEmail(buyer.email) || buyer.email !== buyer.email_confirm) {
      toast.error("Informe e confirme um e-mail válido.");
      return false;
    }
    const phoneDigits = digits(buyer.phone);
    if (phoneDigits.length < 10 || phoneDigits.length > 13) {
      toast.error("Informe um WhatsApp/celular válido com DDD.");
      return false;
    }
    if (buyer.shipping_method === "entrega") {
      const required = [buyer.cep, buyer.street, buyer.number, buyer.neighborhood, buyer.city, buyer.state];
      if (required.some((v) => !String(v || "").trim())) {
        toast.error("Preencha CEP, rua, número, bairro, cidade e estado para entrega.");
        return false;
      }
    }
    if (items.length === 0) {
      toast.error("Carrinho vazio.");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, quantity: i.quantity })),
        buyer,
        method,
      });
      setResult(data);
      setStatus(data.status);
      if (method === "card" && data.status) {
        toast.success("Pedido criado. Acompanhe o status do pagamento.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (config && !campaignOpen) {
    return (
      <div className="App"><Header />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="rounded-2xl p-8" style={{ background: "#2D1216", border: "1px solid #EF4444" }} data-testid="checkout-ended">
            <h1 className="font-head text-2xl font-bold text-white">Vendas encerradas</h1>
            <p className="text-[var(--text-muted)] mt-3">Não é possível finalizar a compra. A campanha do evento foi encerrada.</p>
            <Link to="/pagode-das-cinco-pontas"><Button className="btn-primary font-bold mt-6">Voltar à loja</Button></Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/pagode-das-cinco-pontas" className="inline-flex items-center gap-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-main)] mb-6">
          <ArrowLeft size={16} /> Voltar à loja
        </Link>
        <h1 className="font-head text-3xl font-black mb-8">Finalizar compra</h1>

        {result ? (
          <PaymentResult result={result} status={status} method={method} settings={settings} />
        ) : (
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Buyer form */}
            <div className="lg:col-span-3 space-y-5">
              <div className="card-surface p-6 space-y-4">
                <h2 className="font-head font-bold text-lg">Dados do comprador</h2>
                <div><Label>Nome completo *</Label><Input data-testid="checkout-name" value={buyer.name} onChange={set("name")} className="bg-[var(--bg-secondary)]" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>CPF *</Label><Input data-testid="checkout-cpf" value={buyer.cpf} onChange={set("cpf")} className="bg-[var(--bg-secondary)]" placeholder="000.000.000-00" /></div>
                  <div><Label>WhatsApp/celular *</Label><Input data-testid="checkout-phone" value={buyer.phone} onChange={set("phone")} className="bg-[var(--bg-secondary)]" placeholder="(11) 99999-9999" /></div>
                </div>
                <div><Label>E-mail *</Label><Input data-testid="checkout-email" type="email" value={buyer.email} onChange={set("email")} className="bg-[var(--bg-secondary)]" /></div>
                <div><Label>Confirme o e-mail *</Label><Input data-testid="checkout-email-confirm" type="email" value={buyer.email_confirm} onChange={set("email_confirm")} className="bg-[var(--bg-secondary)]" /></div>
              </div>

              <div className="card-surface p-6 space-y-4">
                <h2 className="font-head font-bold text-lg">Entrega ou retirada</h2>
                <p className="text-sm text-[var(--text-muted)]">Escolha antes de pagar. Se for entrega, o custo aparece no resumo e entra no total.</p>
                <RadioGroup value={buyer.shipping_method} onValueChange={(v) => setBuyer((b) => ({ ...b, shipping_method: v }))}>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="shipping-retirada">
                    <RadioGroupItem value="retirada" /> Retirada no evento <span className="text-xs text-[var(--text-muted)]">sem taxa</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="shipping-entrega">
                    <RadioGroupItem value="entrega" /> Entrega <span className="text-xs text-[var(--text-muted)]">+ {brl(Number(settings?.store_delivery_fee || 0))}</span>
                  </label>
                </RadioGroup>
                {buyer.shipping_method === "retirada" && settings?.store_pickup_info && (
                  <div className="rounded-lg p-3 text-sm bg-[var(--bg-secondary)] border border-[var(--border)] whitespace-pre-line">{settings.store_pickup_info}</div>
                )}
                {buyer.shipping_method === "entrega" && (
                  <div className="space-y-3">
                    {settings?.store_delivery_info && <div className="rounded-lg p-3 text-sm bg-[var(--bg-secondary)] border border-[var(--border)] whitespace-pre-line">{settings.store_delivery_info}</div>}
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>CEP *</Label><Input data-testid="checkout-cep" value={buyer.cep} onChange={set("cep")} className="bg-[var(--bg-secondary)]" /></div>
                      <div><Label>Número *</Label><Input data-testid="checkout-number" value={buyer.number} onChange={set("number")} className="bg-[var(--bg-secondary)]" /></div>
                    </div>
                    <div><Label>Rua *</Label><Input data-testid="checkout-street" value={buyer.street} onChange={set("street")} className="bg-[var(--bg-secondary)]" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Bairro *</Label><Input data-testid="checkout-neighborhood" value={buyer.neighborhood} onChange={set("neighborhood")} className="bg-[var(--bg-secondary)]" /></div>
                      <div><Label>Complemento</Label><Input data-testid="checkout-complement" value={buyer.complement} onChange={set("complement")} className="bg-[var(--bg-secondary)]" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Cidade *</Label><Input data-testid="checkout-city" value={buyer.city} onChange={set("city")} className="bg-[var(--bg-secondary)]" /></div>
                      <div><Label>Estado *</Label><Input data-testid="checkout-state" value={buyer.state} onChange={set("state")} className="bg-[var(--bg-secondary)]" maxLength={2} /></div>
                    </div>
                    <div><Label>Observação para entrega</Label><Input data-testid="checkout-address" value={buyer.address} onChange={set("address")} className="bg-[var(--bg-secondary)]" placeholder="Ex.: portão azul, entregar na portaria..." /></div>
                  </div>
                )}
              </div>

              <div className="card-surface p-6 space-y-4">
                <h2 className="font-head font-bold text-lg">Pagamento</h2>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setMethod("pix")} data-testid="method-pix"
                    className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 ${method === "pix" ? "border-[var(--accent)]" : "border-[var(--border)]"}`}>
                    <QrCode /> <span className="font-semibold">Pix</span>
                  </button>
                  <button onClick={() => setMethod("card")} data-testid="method-card"
                    className={`p-4 rounded-xl border-2 flex flex-col items-center gap-2 ${method === "card" ? "border-[var(--accent)]" : "border-[var(--border)]"}`}>
                    <CreditCard /> <span className="font-semibold">Cartão</span>
                  </button>
                </div>
                {!connected && (
                  <div className="rounded-lg p-4 text-sm bg-[var(--bg-secondary)] border border-[var(--border)] text-[var(--text-muted)]" data-testid="mp-not-connected">
                    O pagamento online será habilitado assim que a conta Mercado Pago for conectada pelo administrador.
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="lg:col-span-2">
              <div className="card-surface p-6 sticky top-24">
                <h2 className="font-head font-bold text-lg mb-4">Resumo do pedido</h2>
                <div className="space-y-3 max-h-64 overflow-y-auto no-scrollbar">
                  {items.map((i) => (
                    <div key={`${i.product_id}-${i.size}`} className="flex gap-3 text-sm">
                      <div className="w-12 h-12 rounded bg-[var(--bg-secondary)] overflow-hidden shrink-0">
                        {i.photo && <img src={fileUrl(i.photo)} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">{i.name}</div>
                        <div className="text-[var(--text-muted)] text-xs">Tam {i.size} · {i.quantity}x</div>
                      </div>
                      <div className="font-semibold">{brl(i.price * i.quantity)}</div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-[var(--border)] mt-4 pt-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[var(--text-muted)]">Produtos</span><span>{brl(total)}</span></div>
                  <div className="flex justify-between"><span className="text-[var(--text-muted)]">Entrega</span><span>{buyer.shipping_method === "entrega" ? brl(deliveryFee) : "Retirada grátis"}</span></div>
                  <div className="flex justify-between font-bold text-lg pt-2"><span>Total</span><span className="text-[var(--accent)]">{brl(finalTotal)}</span></div>
                </div>
                <Button onClick={submit} disabled={loading || !connected} data-testid="checkout-pay-button" className="w-full btn-primary font-bold h-12 mt-5 disabled:opacity-40">
                  {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : null}
                  {connected ? "Pagar agora" : "Pagamento indisponível"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer brand="stamp" />
    </div>
  );
}

function PaymentResult({ result, status, method, settings }) {
  const paid = ["approved", "processed", "accredited"].includes(status);
  const copy = () => {
    navigator.clipboard.writeText(result.qr_code || "");
    toast.success("Código Pix copiado!");
  };
  return (
    <div className="max-w-lg mx-auto card-surface p-8 text-center" data-testid="payment-result">
      {paid ? (
        <>
          <CheckCircle2 className="mx-auto text-green-400" size={56} />
          <h2 className="font-head text-2xl font-bold mt-4">Pagamento confirmado!</h2>
          <p className="text-[var(--text-muted)] mt-2">Pedido {result.order_id.slice(0, 8)} confirmado. Enviamos os dados do pedido para o e-mail informado.</p>
          {result.customer_whatsapp_link && <a href={result.customer_whatsapp_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 btn-primary px-4 py-2 rounded-lg font-bold"><MessageCircle size={18} /> Abrir confirmação no WhatsApp</a>}
        </>
      ) : method === "pix" && result.qr_code ? (
        <>
          <h2 className="font-head text-2xl font-bold">Pague com Pix</h2>
          <p className="text-[var(--text-muted)] text-sm mt-2">Escaneie o QR Code ou copie o código. O pedido confirma automaticamente após o pagamento.</p>
          {result.qr_code_base64 && (
            <img src={`data:image/png;base64,${result.qr_code_base64}`} alt="QR Code Pix" className="mx-auto my-6 w-56 h-56 bg-white p-2 rounded-xl" />
          )}
          <div className="flex items-center gap-2 bg-[var(--bg-secondary)] rounded-lg p-3 text-xs break-all">
            <span className="flex-1 text-left">{result.qr_code}</span>
            <button onClick={copy} className="btn-primary p-2 rounded" data-testid="copy-pix"><Copy size={16} /></button>
          </div>
          <div className="mt-4 text-sm text-[var(--text-muted)] flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={16} /> Aguardando pagamento... ({status})
          </div>
          {result.customer_whatsapp_link && <a href={result.customer_whatsapp_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-4 text-[var(--accent)] font-semibold"><MessageCircle size={16} /> Enviar dados do pedido para meu WhatsApp</a>}
        </>
      ) : (
        <>
          <Loader2 className="mx-auto animate-spin text-[var(--accent)]" size={40} />
          <h2 className="font-head text-xl font-bold mt-4">Processando pagamento</h2>
          <p className="text-[var(--text-muted)] mt-2">Status atual: {status || "pendente"}</p>
        </>
      )}
    </div>
  );
}
